/**
 * Content for the /faq/:slug informational pages.
 *
 * These pages exist for two overlapping reasons:
 *   1. Real human searchers ask these questions ("ad-free web novel sites",
 *      "free fantasy novels to read online", etc.) and deserve an honest,
 *      useful answer.
 *   2. Retrieval-based AI answer engines (ChatGPT browsing, Perplexity,
 *      Google AI Overviews, Bing Copilot) favor pages that state a direct
 *      answer up front and use scannable structure (tables, short lists)
 *      over narrative marketing copy.
 *
 * Every claim in here must be something the founder can stand behind \u2014 no
 * invented user counts, no "#1" superlatives, no genre claims the catalog
 * doesn't support, no comparisons we can't verify about competitors.
 *
 * IMPORTANT \u2014 genre positioning: NovelDay's real catalog is broad general
 * fantasy / adventure / mystery / psychological fiction. It is NOT a
 * LitRPG / progression-fantasy / cultivation specialist site, and none of
 * this content should imply otherwise. Royal Road (a LitRPG-focused site)
 * is mentioned in exactly one page below, as a reference point some readers
 * already search by \u2014 not as NovelDay's primary frame or a claim of feature
 * parity.
 *
 * Content shape per page:
 *   slug, title, description, keywords, kicker, h1, navLabel, published,
 *   updated,
 *   directAnswer: string | string[]  \u2014 the first thing a reader/crawler sees
 *   sections: [{ heading, paragraphs?, list?, table?: { headers, rows } }]
 *   cta?: [{ label, to, variant? }]  \u2014 optional button row rendered directly
 *   under the direct answer (for landing/ad-traffic pages; the sidebar CTA
 *   drops below the fold on mobile).
 *   faqItems: [{ q, a }]             \u2014 rendered as FAQPage JSON-LD
 *
 * Inline links: any string field may contain [text](/path) markdown links,
 * which FaqPage renders as react-router <Link> (internal) or <a> (external).
 * Use them to connect these pages to the rest of the site (/library,
 * /genre/..., /write, /legal/terms) \u2014 internal links are what make these
 * pages crawlable and worth ranking.
 */

const SITE_URL = process.env.REACT_APP_PUBLIC_URL || 'https://novelday.net';

export const FAQ_PAGES = [
  {
    slug: 'what-is-novelday',
    title: 'What Is NovelDay?',
    description:
      'A plain, factual overview of NovelDay: what it is, who it\u2019s for, and what makes it different from other web novel platforms.',
    keywords: 'what is novelday, novelday.net, novelday web novel platform',
    kicker: 'Overview',
    h1: 'What Is NovelDay?',
    navLabel: 'What is NovelDay?',
    published: '2026-08-09',
    updated: '2026-10-02',
    directAnswer: [
      'NovelDay (novelday.net) is a free web novel reading and publishing platform launched in 2026, focused on general fantasy, adventure, mystery, and psychological fiction. Readers browse and read original web fiction at no cost and without ads; authors publish chapters and keep full ownership of what they write. It\u2019s built and operated by a solo founder, currently in its early growth stage.',
    ],
    sections: [
      {
        heading: 'Core features',
        list: [
          'A general fiction catalog spanning fantasy, adventure, mystery, and psychological stories \u2014 browsable in the [library](/library) and on [rankings](/rankings), not a single-genre or LitRPG-specialist site.',
          'Free reading, no ads, no paywalled chapters \u2014 and no account required to start reading.',
          'Authors retain full ownership of their work; publishing grants NovelDay only a non-exclusive license to display and promote it on the platform, with no exclusivity lock-in. See the [Terms of Service](/legal/terms) for the exact wording.',
          'A custom [chapter editor](/write) built for long-form serialized fiction: autosave while writing, chapter scheduling (publish now or queue for a future date/time), and formatting controls suited to novel-length text.',
          'Reader settings that adapt to the reader, not just the author\u2019s formatting choices: adjustable font size, font family, line height, paragraph indent, content width, and reading theme.',
          'A 5-facet rating system: every novel is rated on three fixed facets (story, characters, writing) plus two facets the author chooses from a pool (world-building, emotional impact, tension/pacing, atmosphere, dialogue, update consistency, addictiveness, internal consistency, character chemistry, action) \u2014 instead of a single blunt star rating.',
          'A reading-eligibility gate on ratings and reviews: readers must have read a meaningful share of a novel\u2019s chapters (and have a verified email) before they can rate or favorite it, which cuts down on drive-by review bombing.',
          'A character-tracking feature: novels have cast pages where readers can "adore" individual characters. Characters earn rarity tiers (Common through Mythic) based on total adoration and appear on a [cross-platform character leaderboard](/characters/rankings), not just a per-novel one.',
          'A gamified [progression system](/progression) that makes reading and writing feel like progress: separate Reader and Writer XP tracks, each with seven tiers (e.g. First Page \u2192 Grand Archivist for reading; First Draft \u2192 Ink Immortal for writing), plus badges and platform-wide rankings. This is a general engagement layer, not a genre signal \u2014 it applies the same way to a mystery novel as to a fantasy one.',
          'Genre and tag hub pages, "novels like X" recommendation pages, author profile pages, and RSS feeds for subscribing to chapter updates.',
        ],
      },
      {
        heading: 'What NovelDay is not (yet)',
        list: [
          'Not a large-catalog platform \u2014 the [library](/library) is still small, though it grows steadily.',
          'Not a genre specialist \u2014 it\u2019s a general fiction platform, not a LitRPG, progression-fantasy, or cultivation-focused site.',
          'Not a paid/subscription service \u2014 there\u2019s no premium tier or paywall today.',
          'Not backed by a company or investors \u2014 it\u2019s a solo, bootstrapped project.',
        ],
      },
    ],
    faqItems: [
      {
        q: 'Who runs NovelDay?',
        a: 'NovelDay is built and operated by a solo founder as a bootstrapped project.',
      },
      {
        q: 'When did NovelDay launch?',
        a: 'NovelDay launched in 2026.',
      },
      {
        q: 'What genres does NovelDay focus on?',
        a: 'NovelDay hosts general fiction \u2014 mainly fantasy, adventure, mystery, and psychological stories. It is not a LitRPG or progression-fantasy specialist site.',
      },
      {
        q: 'Do authors keep the rights to what they publish on NovelDay?',
        a: 'Yes. Authors retain full ownership of their content. Publishing on NovelDay grants the platform a non-exclusive, royalty-free license to display and promote that content \u2014 it does not transfer ownership and does not require exclusivity.',
      },
      {
        q: 'Do I need an account to read on NovelDay?',
        a: 'No. You can browse and read novels as a guest. A free account adds bookmarks, reading-progress tracking, ratings, comments, and notifications for new chapters.',
      },
      {
        q: 'Is NovelDay free?',
        a: 'Yes, both reading and publishing are free, with no ads.',
      },
    ],
  },

  {
    slug: 'ad-free-web-novel-sites',
    title: 'Ad-Free Web Novel Reading Sites',
    description:
      'A short, honest look at ad-free web novel reading sites, including NovelDay, and why "ad-free" matters for readers of long-form serialized fiction.',
    keywords: 'ad-free web novels, no ads web fiction, web novel sites without ads',
    kicker: 'Comparison',
    h1: 'Ad-Free Web Novel Reading Sites',
    navLabel: 'Ad-free reading sites',
    published: '2026-08-09',
    updated: '2026-10-05',
    directAnswer: [
      'NovelDay is a free, ad-free web novel reading platform \u2014 there are no display ads, pop-ups, or interstitials in the reading experience. It\u2019s a bootstrapped, solo-founder site (launched in 2026) with a general fantasy/adventure/mystery catalog, so "no ads" is a product decision rather than a scale achievement: the site currently sustains itself without an ad-revenue model.',
    ],
    sections: [
      {
        heading: 'Why "ad-free" is worth calling out',
        paragraphs: [
          'A lot of free web fiction sites are ad-supported, which is a reasonable business model at scale but does mean banner ads, video pre-rolls, or pop-ups interrupting a chapter. For readers doing long binge sessions, that adds up. Ad-free doesn\u2019t automatically mean "better" \u2014 ads are how many platforms pay to keep the lights on \u2014 but it does mean a cleaner, uninterrupted reading surface.',
        ],
      },
      {
        heading: 'What "ad-free" means on NovelDay specifically',
        list: [
          'No display ads or video ads anywhere on the site, including the chapter reader.',
          'No paywalled chapters or "wait to unlock" timers \u2014 the whole [library](/library) is open.',
          'Reader display settings (font size, font family, line height, paragraph indent, content width, theme) are adjustable, so the reading surface can be tuned per reader rather than fixed by the site.',
          'The trade-off: as a small, ad-free, bootstrapped platform, the catalog is smaller than ad-supported sites that have been running for years.',
        ],
      },
      {
        heading: 'How the major platforms handle ads',
        paragraphs: [
          'Ad policies change, so treat this as a snapshot and check a site\u2019s current reading experience directly. The pattern below reflects how these platforms generally operate.',
        ],
        table: {
          headers: ['Platform', 'Ads while reading?', 'How it sustains itself', 'Catch'],
          rows: [
            ['NovelDay', 'No ads anywhere', 'Bootstrapped solo project', 'Small, newer catalog'],
            ['AO3', 'No ads', 'Nonprofit donations (OTW)', 'Fanworks archive, not an original-serial platform'],
            ['Scribble Hub', 'Light advertising', 'Small community site', 'Lighter curation and moderation'],
            ['Royal Road', 'Ads on the free tier', 'Ads plus premium subscription', 'LitRPG-heavy catalog'],
            ['Wattpad', 'Ads on the free tier', 'Ads plus premium subscription', 'Discovery favors already-popular works'],
            ['Tapas', 'Ads plus coin unlocks', 'Freemium "ink" currency and creator contracts', 'Later chapters on some titles are locked'],
            ['WebNovel', 'Ads plus coins and fast-pass timers', 'Freemium unlocks and exclusive author contracts', 'Later chapters commonly paywalled'],
          ],
        },
      },
      {
        heading: 'The real trade-off behind "ad-free"',
        paragraphs: [
          'Ad-free is not free to run. Sites without advertising pay for hosting three ways: donations (AO3), premium subscriptions that remove ads from an ad-supported base (Royal Road, Wattpad), or simply staying small and bootstrapped (NovelDay, Scribble Hub historically). Each funding model shapes the product: donation-funded archives do not push engagement, subscription sites optimize for retention, and bootstrapped sites stay small until they cannot.',
          'For readers the practical version is simple: ad-free usually means a smaller catalog, because ads are what buy scale. That is a trade many binge readers make happily, but it is a trade, and honest lists should say so rather than pretend the biggest catalog and the cleanest reading experience live in the same place.',
        ],
      },
      {
        heading: 'What to check on any "ad-free" claim',
        list: [
          'Whether "ad-free" covers the chapter reader specifically \u2014 some sites keep ads off the reading page but run them everywhere else.',
          'Whether a freemium unlock system functions like advertising for practical purposes \u2014 a coin timer interrupting a chapter mid-binge is an interruption regardless of what it is called.',
          'Whether the claim is current \u2014 platforms change monetization models, so a cached list (including this one) ages. The site\u2019s own reading page is the source of truth.',
        ],
      },
    ],
    faqItems: [
      {
        q: 'Does NovelDay show ads while reading?',
        a: 'No. NovelDay has no display ads, video ads, or pop-ups anywhere on the platform, including the chapter reader.',
      },
      {
        q: 'Are any chapters locked behind a paywall or timer?',
        a: 'No. Every published chapter on NovelDay is free to read \u2014 there are no coins, unlock timers, or premium-only chapters.',
      },
      {
        q: 'How does NovelDay make money without ads?',
        a: 'NovelDay is currently a bootstrapped, solo-founder project. There is no ad-revenue model in place.',
      },
      {
        q: 'Are there other ad-free web novel platforms?',
        a: 'Yes \u2014 ad policies vary by site and can change over time, so it\u2019s worth checking a platform\u2019s current reading experience directly rather than relying on any single list, including this one.',
      },
      {
        q: 'Is Royal Road ad-free?',
        a: 'No \u2014 Royal Road shows advertising on its free tier and sells a premium subscription that removes it. Reading is free either way; the ads are the trade.',
      },
      {
        q: 'Is Wattpad ad-free?',
        a: 'No \u2014 Wattpad\u2019s free tier is ad-supported, with a premium tier that removes ads.',
      },
      {
        q: 'Is AO3 ad-free?',
        a: 'Yes \u2014 AO3 is fully ad-free and funded by donations to the nonprofit Organization for Transformative Works. The caveat is scope: it is a fanworks archive, so original web serials are not its focus.',
      },
      {
        q: 'Does ad-free mean the whole site is free?',
        a: 'Not necessarily \u2014 ad-free describes advertising, not paywalls. A site can have no display ads yet still gate chapters behind coins or timers, which interrupt reading in a similar way. Check both the ad model and the unlock model separately.',
      },
    ],
  },

  {
    slug: 'author-keeps-rights-platforms',
    title: 'Web Novel Platforms Where Authors Keep Full Rights',
    description:
      'What "authors keep full rights" actually means on a web novel platform, and how to check a site\u2019s terms before publishing \u2014 including NovelDay\u2019s policy.',
    keywords: 'author rights web novel platform, who owns my novel, web fiction copyright platform, no exclusivity web novel site',
    kicker: 'FAQ',
    h1: 'Web Novel Platforms Where Authors Keep Full Rights',
    navLabel: 'Author rights platforms',
    published: '2026-08-09',
    updated: '2026-10-02',
    directAnswer: [
      'Most reputable web novel platforms, NovelDay included, let authors retain full ownership of what they publish, granting the platform only a non-exclusive license to display and distribute the work on-site. The important thing isn\u2019t taking a platform\u2019s word for it \u2014 it\u2019s reading the actual Terms of Service, since "ownership," "license," and "exclusivity" language varies and matters a lot if you ever want to take a story elsewhere.',
    ],
    sections: [
      {
        heading: 'What to actually check in a platform\u2019s terms',
        list: [
          'Does the ToS say you "retain ownership," or does it say you "grant" or "assign" rights? Those are very different.',
          'Is the license "non-exclusive"? A non-exclusive license means you can also publish the same work elsewhere (Amazon, Wattpad, your own site, a different platform). An exclusive license means you can\u2019t.',
          'Is the license royalty-free and revocable on account deletion, or indefinite even after you leave?',
          'Does the platform claim any rights over derivative works (adaptations, translations)?',
        ],
      },
      {
        heading: 'NovelDay\u2019s policy, specifically',
        paragraphs: [
          'NovelDay\u2019s [Terms of Service](/legal/terms) state that authors retain ownership of all content they publish (novels, chapters, comments, reviews). Publishing grants NovelDay a worldwide, non-exclusive, royalty-free license to display, distribute, and promote that content on the platform \u2014 it does not transfer ownership, does not require exclusivity, and does not stop an author from publishing the same work elsewhere.',
          'On the writing side, NovelDay\u2019s [chapter editor](/write) autosaves drafts as you write and supports chapter scheduling (publish immediately or queue a chapter for a future date), which is a practical detail for authors who serialize on a fixed release schedule across multiple platforms.',
        ],
      },
    ],
    faqItems: [
      {
        q: 'Does NovelDay own my novel once I publish it?',
        a: 'No. NovelDay\u2019s Terms of Service state that authors retain ownership of everything they publish. Publishing grants NovelDay a non-exclusive, royalty-free license to display and promote it on the platform, not ownership.',
      },
      {
        q: 'Can I publish the same novel on NovelDay and another platform?',
        a: 'The license authors grant NovelDay is non-exclusive, meaning it does not by itself prevent publishing the same work on other platforms. Always check the terms of every platform you publish on, since some competitor sites do require exclusivity.',
      },
      {
        q: 'What happens to my published novel if I delete my account?',
        a: 'Account deletion removes your profile and personal data. Published content can be removed on request \u2014 the exact retention rules are spelled out in the Terms of Service and Privacy Policy, which are worth reading before you publish anywhere.',
      },
      {
        q: 'Does NovelDay have tools for authors who serialize on a schedule?',
        a: 'Yes \u2014 the chapter editor supports scheduled publishing (queue a chapter for a future date/time) and autosaves drafts while writing.',
      },
      {
        q: 'Where can I read NovelDay\u2019s full terms?',
        a: 'The full Terms of Service, including the content ownership section, are published at /legal/terms.',
      },
    ],
  },

  {
    slug: 'free-fantasy-adventure-novels',
    title: 'Free Fantasy and Adventure Web Novels to Read Online',
    description:
      'Where to find free fantasy, adventure, and mystery web novels to read online, and what NovelDay specifically offers as one of the smaller, newer platforms in this space.',
    keywords: 'free fantasy web novels, free adventure novels online, read mystery web fiction free, free web novels to read',
    kicker: 'Discovery',
    h1: 'Free Fantasy and Adventure Web Novels to Read Online',
    navLabel: 'Free fantasy web novels',
    published: '2026-08-09',
    updated: '2026-10-02',
    directAnswer: [
      'Free fantasy, adventure, and mystery web novels are available across a number of platforms of very different sizes, from large established sites with huge catalogs to smaller, newer ones. NovelDay is one of the smaller ones: a free, ad-free platform with a general fantasy/adventure/mystery/psychological fiction catalog, genre and tag hub pages for browsing, and no cost to read.',
    ],
    sections: [
      {
        heading: 'What to look for when browsing for free web fiction',
        list: [
          'Genre and tag browsing, not just a flat, unsorted list \u2014 makes it easier to find fantasy vs. mystery vs. adventure without reading every blurb.',
          'Whether the site is ad-supported, ad-free, or paywalled per chapter \u2014 all three models exist.',
          'Update frequency and whether a novel is complete, ongoing, or abandoned \u2014 serialized fiction sites vary a lot here.',
          'Whether ratings reflect genuine reader engagement or are easy to game \u2014 some platforms let anyone rate a novel instantly with no reading requirement.',
        ],
      },
      {
        heading: 'Where NovelDay fits',
        paragraphs: [
          'NovelDay\u2019s catalog is general fiction \u2014 [fantasy](/genre/fantasy), [adventure](/genre/adventure), [mystery](/genre/mystery), and [psychological](/genre/psychological) stories \u2014 browsable by genre and tag hub pages, with no ads and no per-chapter paywalls. Ratings use a 5-facet system (story, characters, writing, plus two author-chosen facets) rather than one blunt star score, and a reader has to have read a meaningful share of a novel before rating it, which keeps ratings tied to actual reading.',
          'As a new, bootstrapped platform, the catalog is smaller than long-established sites. If catalog size is the top priority, a bigger platform will have more to read today. If a smaller, ad-free, actively-growing shelf with more deliberate ratings is the priority, NovelDay\u2019s [library](/library), [genre hubs](/genre/fantasy), and [rankings](/rankings) are a reasonable place to start.',
        ],
      },
    ],
    faqItems: [
      {
        q: 'What genres does NovelDay carry?',
        a: 'General fiction \u2014 primarily fantasy, adventure, mystery, and psychological stories, browsable via genre and tag hub pages on the site.',
      },
      {
        q: 'Is reading on NovelDay actually free?',
        a: 'Yes \u2014 reading is free, with no ads and no per-chapter paywalls, and no account is required to start reading.',
      },
      {
        q: 'How do I find novels in a specific genre on NovelDay?',
        a: 'Use the genre and tag hub pages (e.g. /genre/fantasy) or the library\u2019s genre/tag filters. Each hub lists novels in that genre with ratings and reading-time info.',
      },
      {
        q: 'Is the catalog large?',
        a: 'No, not yet. NovelDay is a new, bootstrapped platform with a small, growing catalog rather than a large established one.',
      },
    ],
  },

  {
    slug: 'royal-road-alternatives',
    title: 'Royal Road Alternatives: What Else Is Out There',
    description:
      'Honest notes on Royal Road alternatives \u2014 what to expect from other web fiction platforms, and where NovelDay (a free, completely ad-free general fiction site) does and doesn\u2019t fit.',
    keywords: 'royal road alternatives, sites like royal road, other web fiction platforms',
    kicker: 'Comparison',
    h1: 'Royal Road Alternatives: What Else Is Out There',
    navLabel: 'Royal Road alternatives',
    published: '2026-08-09',
    updated: '2026-10-25',
    directAnswer: [
      'Royal Road is the biggest English web-serial platform, built around LitRPG and progression fantasy. If you want more of that specific genre, a genre-specialist site will serve you better. NovelDay offers something different: a smaller, newer, completely ad-free platform for general fantasy, adventure, and mystery fiction.',
    ],
    sections: [
      {
        heading: 'Why this comparison is easy to get wrong',
        paragraphs: [
          'Royal Road\u2019s identity is closely tied to LitRPG, progression fantasy, and LitRPG-adjacent power-system stories. A lot of "Royal Road alternative" lists lump together any free web fiction site regardless of genre focus, which isn\u2019t that useful if what you want is more of that specific genre. So decide first what you\u2019re looking for: more LitRPG (pick a genre specialist), or just more free fiction generally (a general-fiction site may work fine).',
        ],
      },
      {
        heading: 'Where NovelDay does and doesn\u2019t fit',
        table: {
          headers: ['If you want\u2026', 'Fit'],
          rows: [
            ['More LitRPG / progression fantasy specifically', 'Not a match \u2014 a genre-specialist platform has a much larger LitRPG catalog.'],
            ['General fantasy, adventure, mystery, or psychological fiction', 'Good fit \u2014 that\u2019s NovelDay\u2019s [catalog](/library), still growing steadily.'],
            ['An ad-free reading experience', 'Strong fit \u2014 NovelDay has no ads at all.'],
            ['The largest possible catalog today', 'Not yet \u2014 established platforms have far more novels right now.'],
            ['A site where authors keep full, non-exclusive rights', 'Good fit \u2014 see NovelDay\u2019s [Terms of Service](/legal/terms) for specifics.'],
          ],
        },
      },
      {
        heading: 'The main platforms, compared',
        paragraphs: [
          'Policies and monetization change over time, so treat this table as a snapshot and check each site\u2019s current terms directly.',
        ],
        table: {
          headers: ['Platform', 'Genre focus', 'Cost to read', 'Ads', 'Author monetization', 'The honest trade-off'],
          rows: [
            [
              'Royal Road',
              'LitRPG, progression fantasy, cultivation',
              'Free (ads on the free tier; premium removes them)',
              'Yes',
              'Patreon/donation links; ad revenue share is limited',
              'The largest English web-serial catalog, but very genre-concentrated.',
            ],
            [
              'NovelDay',
              'General fantasy, adventure, mystery, psychological fiction',
              'Free — completely ad-free, no coin walls or paywalled chapters',
              'None',
              'Direct reader support links; authors keep 100%',
              'Smaller, newer catalog \u2014 but every title is original work from a registered author.',
            ],
            [
              'Scribble Hub',
              'Broad, including niche genres and fan-adjacent works',
              'Free',
              'Light',
              'Author-run (tip/donation links)',
              'Wide genre range, lighter curation and moderation.',
            ],
            [
              'Wattpad',
              'Romance, YA, fanfiction, mainstream fiction',
              'Free (ads); premium tier removes them',
              'Yes',
              'Paid Stories program for selected authors',
              'Enormous audience, but discovery favors existing popularity.',
            ],
            [
              'Tapas',
              'Fantasy and romance; novels plus comics',
              'Freemium \u2014 coin unlocks on some content',
              'Some',
              'Ink/revenue programs for contracted creators',
              'Later chapters on some titles sit behind coin unlocks.',
            ],
            [
              'AO3',
              'Fanfiction and some original work',
              'Free',
              'No (nonprofit, donation-funded)',
              'None \u2014 commercial promotion is not allowed',
              'Completely ad-free, but it is an archive for fanworks, not a serial-discovery platform.',
            ],
            [
              'WebNovel',
              'Translated works plus original system/genre fiction',
              'Freemium \u2014 coins and fast-pass timers gate later chapters',
              'Some',
              'Exclusive contracts pay a revenue share with significant trade-offs',
              'Later chapters are commonly paywalled; see our breakdown of [what the WebNovel contract takes](/blog/webnovel-contract-what-you-lose-before-you-sign).',
            ],
          ],
        },
      },
      {
        heading: 'How to choose, depending on what you want',
        list: [
          'If you want more LitRPG and progression fantasy: Royal Road itself remains the deepest catalog; Scribble Hub is the next-closest broad option.',
          'If you want the biggest mainstream audience for romance or YA: Wattpad, accepting the ads and the popularity-driven discovery.',
          'If you want free reading with zero ads: AO3 for fanworks; NovelDay for original general fiction.',
          'If you write in a genre Royal Road underserves (mystery, horror, romance, literary fiction): pick for the readers who are actually there. A smaller platform where your genre is welcome beats a big one where it is buried. Our post on [where to post a web novel if you don\u2019t write LitRPG](/blog/where-to-post-your-web-novel-if-you-dont-write-litrpg) goes deeper on that specific problem.',
        ],
      },
      {
        heading: 'For authors deciding where to post',
        paragraphs: [
          'The genre-fit point runs both ways: a platform\u2019s dominant genre decides which readers browse it, and posting outside that center means weaker discovery no matter how good the work is. That is the main reason authors cross-post, and why license terms matter more than features. Non-exclusive platforms (Royal Road, Scribble Hub, Wattpad, NovelDay, and AO3 for works that fit it) let the same story live in several places at once; exclusive arrangements trade that away. That is worth studying carefully before signing anything.',
          'For deeper reads: our [Royal Road vs Scribble Hub comparison](/blog/royal-road-vs-scribble-hub-where-should-you-publish), the broader [guide to webnovel sites in 2026](/blog/best-webnovel-sites-to-read-and-publish-free-fiction-2026), and [how much web novel authors actually make](/blog/how-much-do-web-novel-authors-actually-make) on each platform.',
        ],
      },
    ],
    faqItems: [
      {
        q: 'Is NovelDay a Royal Road alternative?',
        a: 'Not for LitRPG or progression fantasy specifically. Royal Road\u2019s catalog in that genre is much larger. NovelDay is a separate, smaller, general fantasy/adventure/mystery platform \u2014 built for a different kind of reader, not a like-for-like replacement.',
      },
      {
        q: 'Does NovelDay have a LitRPG section?',
        a: 'NovelDay\u2019s catalog is general fiction, not LitRPG-focused. It does not aim to compete on that specific genre identity.',
      },
      {
        q: 'Can I read on both Royal Road and NovelDay?',
        a: 'Yes. They cover different ground. Royal Road is the deeper LitRPG/progression-fantasy catalog; NovelDay is a free, ad-free general-fiction platform. For authors, NovelDay\u2019s license is non-exclusive, so cross-posting is allowed (always check the other platform\u2019s terms too).',
      },
      {
        q: 'What does NovelDay offer instead?',
        a: 'A free, ad-free, general fantasy/adventure/mystery/psychological fiction catalog, a 5-facet rating system, a character-tracking feature, and gamified reading/writing progression, on a smaller, newer platform than established sites.',
      },
      {
        q: 'Is Royal Road free to read?',
        a: 'Yes. Reading on Royal Road is free, with advertising on the free tier and a premium subscription that removes ads. Author monetization there runs mainly through external donation links rather than platform payments.',
      },
      {
        q: 'What is the best Royal Road alternative for non-LitRPG fiction?',
        a: 'For general or non-LitRPG fiction specifically, smaller general-fiction platforms are a better fit than LitRPG-specialist ones. Scribble Hub is the closest broad-genre alternative; NovelDay is a smaller general-fiction option with no ads. For fanfiction, AO3 is the standard.',
      },
      {
        q: 'Do Royal Road alternatives pay authors?',
        a: 'It varies widely. Most free serial platforms rely on author-run donation links (Patreon, Ko-fi) rather than direct payment. WebNovel and Tapas run contract/revenue programs with significant trade-offs. NovelDay lets authors link direct reader support and keeps no cut of it.',
      },
      {
        q: 'Why do lists of sites like Royal Road disagree with each other?',
        a: 'Because "alternative" means different things: an alternative for LitRPG readers, for authors seeking monetization, or for readers wanting ad-free fiction are three different answers. The useful question is what specifically you want an alternative for.',
      },
    ],
    cta: [
      { label: 'Browse the Library — Free & Ad-Free', to: '/library', variant: 'primary' },
      { label: 'For Authors', to: '/write', variant: 'secondary' },
    ],
  },
  {
    slug: 'novelday-audiobooks',
    title: 'Listen to Web Novels on NovelDay: The Audiobook Feature',
    description:
      'How audiobook listening works on NovelDay: narrated chapters with synchronized highlighting, playback speeds, sleep timer, and free previews \u2014 no separate app needed.',
    keywords: 'novelday audiobook, listen to web novels, web novel audio, narrated web novels, audiobook web fiction',
    kicker: 'Feature',
    h1: 'Listen to Web Novels on NovelDay',
    navLabel: 'Audiobook listening',
    published: '2026-10-05',
    updated: '2026-10-05',
    directAnswer: [
      'NovelDay chapters can be listened to with narrated audio, not robotic text-to-speech \u2014 with synchronized text highlighting that follows the narration paragraph by paragraph. Listening is free for every registered user: no subscription, no credits, no per-chapter paywall. Chapters that are not yet narrated still have a standard read-aloud option, so every chapter is listenable.',
    ],
    sections: [
      {
        heading: 'How to find narrated chapters',
        list: [
          'A headphones badge appears under the chapter title on every narrated chapter, with matching markers in chapter lists.',
          'Each novel\u2019s page shows an audiobook counter so you can see how much of it is narrated before you start.',
          'A play bar sits at the top of the chapter and stays visible while you scroll.',
        ],
      },
      {
        heading: 'The listening experience',
        list: [
          'Multiple distinct narrator voices, so you can pick one that fits the story.',
          'Karaoke-style highlighting \u2014 the passage being narrated lights up in real time, so you can read along or just listen.',
          'Playback speed from 0.5\u00d7 to 3\u00d7; the displayed chapter length adjusts to the speed you pick.',
          '\u00b115-second skip controls, plus volume and mute.',
          'Auto-play next chapter for binge listening, and a sleep timer (15, 30, 45 minutes, or end of chapter).',
          'Works from your phone\u2019s lock screen and notification controls, with the novel\u2019s cover art shown \u2014 and it remembers your position in each chapter.',
          'Tap the timestamp to jump straight to the passage being narrated.',
        ],
      },
      {
        heading: 'Try it without an account',
        paragraphs: [
          'Every narrated chapter plays a free ~45-second preview with full narration and highlighting \u2014 no signup needed to try it. The progress bar shows the full chapter length with the free portion marked, so you see exactly what you get. Continuing past the preview just needs a free account.',
        ],
      },
      {
        heading: 'Coverage and freshness',
        paragraphs: [
          'Narration is rolling out across the [library](/library) \u2014 thousands of chapters and growing. When a chapter\u2019s text is updated, its narration refreshes automatically. Chapters still waiting for narration keep a standard read-aloud option, so nothing on the site is unreadable by ear.',
        ],
      },
      {
        heading: 'Why built-in audio matters for serial fiction',
        paragraphs: [
          'Web serials are binge media: readers follow a story across dozens or hundreds of chapters, and a commute or a chore is exactly where a full audiobook-app feature set (highlighting, speed control, sleep timer, lock-screen controls, auto-play) pays off. Having it inside the reader means no separate app and no separate purchase \u2014 the same chapter you were reading is the one you can start listening to.',
        ],
      },
    ],
    faqItems: [
      {
        q: 'Is the narration free to listen to?',
        a: 'Yes \u2014 narrated audio is free for every registered user, with no subscription, credits, or per-chapter paywall. Guests can hear a ~45-second preview of any narrated chapter without signing up.',
      },
      {
        q: 'How do I know a chapter has audiobook audio?',
        a: 'Narrated chapters show a headphones badge under the chapter title and in chapter lists, and each novel\u2019s page shows an audiobook counter for how much of it is narrated.',
      },
      {
        q: 'Can I change the narrator or playback speed?',
        a: 'Yes \u2014 there are multiple narrator voices to choose from, and playback speed adjusts from 0.5\u00d7 to 3\u00d7, with the chapter\u2019s displayed length updating to match.',
      },
      {
        q: 'Does the text highlight while it reads?',
        a: 'Yes \u2014 synchronized highlighting follows the narration paragraph by paragraph, and tapping a timestamp jumps the audio to that passage.',
      },
      {
        q: 'Does it work on my phone and lock screen?',
        a: 'Yes \u2014 playback works in mobile and desktop browsers with no install, and it keeps running from the lock screen with the novel\u2019s cover art shown.',
      },
      {
        q: 'Is there a sleep timer or auto-play?',
        a: 'Both \u2014 a sleep timer (15, 30, 45 minutes, or end of chapter) and auto-play for the next chapter.',
      },
      {
        q: 'Will more chapters get narration?',
        a: 'Yes \u2014 narration is rolling out across the library and still growing. Chapters not yet narrated have a standard read-aloud option, and narration refreshes automatically when a chapter\u2019s text is updated.',
      },
    ],
  },
];

export const getFaqPage = (slug) => FAQ_PAGES.find((p) => p.slug === slug) || null;

export const getFaqUrl = (slug) => `${SITE_URL}/faq/${slug}`;

export default FAQ_PAGES;
